//Library imports
import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useLocation } from 'react-router-dom';

//Services
import { getDisciplines } from '../../../api/services/disciplineService';
import { getDistrictOptions } from '../../../api/services/districtService';

//Models and data types
import { selectOption } from '../../../types/selectOption';

//Components
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import DataTable from '../../../components/dataTable';
import { FieldInput } from '../../../components/fieldInput/index';
import { getTherapistNamesAndIds, searchTherapists } from '../../../api/services/therapistService';
import TherapistSearchRequest from '../../../api/requests/therapistSearchRequest';
import Therapists from '../../../api/models/therapists';
import Therapist from '../../../api/models/therapist';
import Discipline from '../../../api/models/discipline';
import District from '../../../api/models/district';
import { Notification } from '../../../components/notifications';
import User from '../../../api/models/user';
import PaginatedResponse from '../../../api/responses/paginatedResponse';
import { Pagination } from '../../../components/pagination';
import FetchInput from '../../../components/FetchInput';
import { getDivisionOptions } from '../../../api/services/divisionService';
import { therapistPageSizeOptions } from '../../../common/constants';
import { convertPageToZeroIndexing } from '../../../common/utils';

//Get our view state
export type ViewState = {
    message?: string;
    discipline?: Discipline;
    disciplineId?: number;
    district?: District;
};

type PageSizeProps = {
    options: number[];
    outerDivClasses?: string;
    changeHandler: Function;
    currentPageSize: number;
}

/**
 * Methods to fetch search criteria with which to populate
 */
export const fetchDisciplines = async () => getDisciplines();
export const fetchDistricts = async () => getDistrictOptions();
export const fetchDivisions = async () => getDivisionOptions();
export const fetchTherapists = async () => await getTherapistNamesAndIds();

//const doSearch = async (request: TherapistSearchRequest) => await searchTherapists(request);

/**
 * Primary Component
 */
const SearchTherapists = () => {
    //Process our DataTable columns and data
    const columns = () => [
        {
            Header: 'Name',
            accessor: (u: any) => {
                return `${u?.user?.lastName ?? u?.lastName}, ${u?.user?.firstName ?? u?.firstName}`;
            }
        },
        {
            Header: 'Division',
            accessor: (u: any) => {
                const therapist: Therapists = u as Therapists;
                return therapist.divisionNames && therapist.divisionNames.length > 0 ? <ul className="list-disc">
                    {therapist.divisionNames?.map((d: string) => <li key={`therapist-${therapist.userId}-division-${d.replace(/ /g, '')}`}>{d}</li>)}
                </ul> : 'N/A';
                //return `${therapist.divisionName && therapist.divisionName !== '' ? therapist.divisionName : 'N/A'}`;
            }
        },
        {
            Header: 'TriCore ID',
            accessor: (u: any) => {
                const therapist: Therapists = u as Therapists;
                return `${therapist.triCoreId && therapist.triCoreId ? therapist.triCoreId : 'N/A'}`;
            }
        },
        {
            Header: 'Status',
            accessor: (u: any) => {
                const therapist: Therapists = u as Therapists;
                return `${therapist.status && therapist.status !== '' ? therapist.status : 'N/A'}`;
            }
        },
        {
            Header: 'Disciplines',
            accessor: (u: any) => {
                const user: User = u;
                let disciplinesString = '';

                //Get all the disciplines, generate a string and return it
                //user.therapists?.map((t: Therapist) => (disciplinesString += t.discipline?.name + ', ').slice(0, -2));

                return user.therapists && user.therapists.length > 0 ? <ul className="list-disc">
                    {user.therapists?.map((t: Therapist) => <li key={`therapist-${user.userId}-discipline-${t.discipline?.disciplineId}`}>{t.discipline?.name}</li>)}
                </ul> : 'N/A';
            }
        },
        {
            accessor: 'userId',
            className: 'hidden',
        },
    ];

    //App hooks
    const history = useHistory();
    const { state } = useLocation<ViewState>();

    //Select element options
    const [therapistOptions, setTherapistOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [disciplineOptions, setDisciplineOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [districtOptions, setDistrictOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);
    const [divisionOptions, setDivisionOptions] = useState<selectOption[]>([{ value: -1, label: '' }]);

    //Data hooks
    const [searchData, setSearchData] = useState<Therapists[]>([]);
    const [therapist, setTherapists] = useState<number>(-1);
    const [disciplineId, setDisciplineId] = useState<number>(-1);
    const [districtId, setDistrictId] = useState<number>(-1);
    const [divisionId, setDivisionId] = useState<number>(-1);
    const [disciplineName, setDisciplineName] = useState<string>('');
    const [districtName, setDistrictName] = useState<string>('');
    const [divisionName, setDivisionName] = useState<string>('');
    const [defaultDisciplineOption, setDefaultDisciplineOption] = useState<selectOption>();
    const [defaultDistrictOption, setDefaultDistrictOption] = useState<selectOption>();
    const [defaultDivisionOption, setDefaultDivisionOption] = useState<selectOption>();
    const [status, setStatus] = useState('');
    const [tricoreId, setTricoreId] = useState<number | undefined>();
    const [name, setName] = useState('');
    const [totalRecordCount, setTotalRecordCount] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const handlePageSizeChange = (pageSizeSelection: string) => {
        const pageSize = Number.parseInt(pageSizeSelection);
        setPageNumber(1); // Reset page number on page size change
        setPageSize(pageSize);
    }

    const handlePageChange = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    /**
     * When the state changes, update the selected discipline select box
     */
    useEffect(() => {
        if (state && state.discipline && state.discipline.disciplineId && state.discipline.disciplineId !== undefined) {
            setDisciplineId(state.discipline.disciplineId);
            setDisciplineName(state.discipline.name);
        }
    }, [state]);

    /**
     * When the page loads, check if we have a discipline passed through
     * If so, set the discipline id
     */
    useEffect(() => {
        let stateDisciplineId: number = disciplineId;
        let stateDistrictId: number = districtId;

        if (state) {
            if (state.disciplineId) {
                stateDisciplineId = state.disciplineId!;
            }
            if (state.discipline) {
                stateDisciplineId = state.discipline.disciplineId!;
            }
            if (state.district) {
                stateDistrictId = state.district.districtId!;
            }
        }

        setDisciplineId(stateDisciplineId);
        setDistrictId(stateDistrictId);

        setTimeout(() => searchTherapistsAsync.run({
            disciplineId: stateDisciplineId,
            districtId: stateDistrictId,
        }), 1);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize]);

    /**
     * Options lists (for our select elements)
     */
    const getTherapistsAutoCompleteAsync = useAsync({
        promiseFn: fetchTherapists,
        onResolve: (u) => setTherapistOptions([...u.data])
    });

    const disciplinesAsync = useAsync({
        promiseFn: fetchDisciplines,
        onResolve: (u) => {
            let options: selectOption[] = disciplineOptions;

            //set options           
            (u.data.disciplines.map((g) => options.push({ value: g.disciplineId ?? -1, label: g.name })));
            options.sort((a, b) => {
                return a.label.localeCompare(b.label);
            });
            setDisciplineOptions(options);

            if (disciplineId > 0) {
                const dOption: selectOption = disciplineOptions.filter((d) => d.value === disciplineId)[0];
                setDefaultDisciplineOption(dOption);
            }
        },
    });

    const districtsAsync = useAsync({
        promiseFn: fetchDistricts,
        onResolve: (u) => {
            let options: selectOption[] = districtOptions;
            u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
            setDistrictOptions(options);

            if (districtId > 0) {
                const dOption: selectOption = districtOptions.filter((d) => d.value === disciplineId)[0];
                setDefaultDistrictOption(dOption);
            }
        },
    });

    const divisionsAsync = useAsync({
        promiseFn: fetchDivisions,
        onResolve: (u) => {
            let options: selectOption[] = divisionOptions;
            u.data.map((g) => options.push({ value: g.value ?? -1, label: g.label }));
            //options.shift();
            setDivisionOptions(options);

            if (divisionId > 0) {
                const dOption: selectOption = divisionOptions.filter((d) => d.value === divisionId)[0];
                setDefaultDivisionOption(dOption);
            }
        },
    });

    const handleTherapistsSearchResponse = (response: PaginatedResponse<User>) => {
        const paginatedResponse = response as PaginatedResponse<User>;
        const therapists = paginatedResponse?.data?.records ?? [] as User[];
        const recordCount: number = paginatedResponse?.data?.totalRecordCount ?? 0;

        setSearchData(therapists);
        setTotalRecordCount(recordCount);
    };

    const therapistsSearchDefer = async (args: any[]) => {

        const request: TherapistSearchRequest = {
            nameSearchFragment: name,
            therapistId: (therapist === -1 ? undefined : therapist),
            disciplineId: (args === undefined || args.length === 0 || args[0].disciplineId === -1
                ? (disciplineId === -1 ? undefined : disciplineId)
                : args[0].disciplineId),
            districtId: (args === undefined || args.length === 0 || args[0].districtId === -1
                ? (districtId === -1 ? undefined : districtId)
                : args[0].districtId),
            disciplineName: disciplineName,
            districtName: districtName,
            divisionName: divisionName,
            status,
            tricoreId: (!tricoreId ? undefined : tricoreId),
            pageNumber: convertPageToZeroIndexing(pageNumber),
            pageSize,
        }
        return await searchTherapists(request);
    }

    //AutoComplete Methods 
    const searchTherapistsAsync = useAsync({
        deferFn: therapistsSearchDefer,
        onResolve: handleTherapistsSearchResponse,
    });

    useEffect(() => getTherapistsAutoCompleteAsync.run(name), [name]);

    return (
        <Layout isLoading={
            searchTherapistsAsync.isPending ||
            getTherapistsAutoCompleteAsync.isPending ||
            disciplinesAsync.isPending ||
            districtsAsync.isPending ||
            divisionsAsync.isPending
        }>
            <Fragment>
                {state?.message && <Notification>{state?.message}</Notification>}

                {/* Primary Container */}
                <div className='z-0 px-4 py-5 bg-white sm:p-6'>
                    {/* Search Fields */}
                    <div className='z-0 grid grid-cols-6 gap-6'>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FetchInput
                                value={name}
                                rounded='sm'
                                placeholder=''
                                label={`Therapist`}
                                key={"therapist_ac"}
                                fetchProperty={`altName`}
                                altFetchProperty={`name`}
                                valueProperty={`name`}
                                fetchData={therapistOptions}
                                onClear={() => setName('')}
                                onChange={(value: string) => setName(value)}
                                icon={'search'}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Disicpline</label>
                            <select
                                className='block primary'
                                value={disciplineName}
                                onChange={(e) => setDisciplineName(e.currentTarget.value)}
                                placeholder='Select...'
                            >
                                {disciplineOptions.sort((a, b) => {
                                    return a.label.localeCompare(b.label);
                                }).map((item, idx) => <option key={`pageSize_${item.label}`}>{item.label}</option>)}
                            </select>
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <label className='primary'>Status</label>
                            <select
                                className='block primary'
                                value={status}
                                onChange={(e) => setStatus(e.currentTarget.value)}
                                placeholder='Select...'
                            >
                                <option></option>
                                <option value="Active">Active</option>
                                <option value="Archived">Archived</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FieldInput
                                label='Tricore ID/CMS ID'
                                value={tricoreId?.toString()}
                                onChange={(e: any) => setTricoreId(e.currentTarget.value)}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FetchInput
                                value={districtId === -1 ? '' : districtOptions.filter((d) => d.value === districtId)[0]?.label}
                                rounded='sm'
                                placeholder=''
                                label={`District`}
                                fetchProperty={`label`}
                                valueProperty={`value`}
                                fetchData={districtOptions}
                                onClear={() => setDistrictName('')}
                                onChange={(value: string) => setDistrictName(value)}
                                icon={'search'}
                            />
                        </div>
                        <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
                            <FetchInput
                                value={divisionId === -1 ? '' : divisionOptions.filter((d) => d.value === divisionId)[0]?.label}
                                rounded='sm'
                                placeholder=''
                                label={`Division`}
                                fetchProperty={`label`}
                                valueProperty={`value`}
                                fetchData={divisionOptions}
                                onClear={() => setDivisionName('')}
                                onChange={(value: string) => setDivisionName(value)}
                                icon={'search'}
                            />
                        </div>
                    </div>

                    {/* Button Row */}
                    <div className='grid grid-cols-2 gap-2 mt-6'>
                        <div className='col-span-1 sm:col-span-1 lg:col-span-1'>
                            <SubmitButton
                                text='Search'
                                className='mr-5 primary button'
                                onClick={() => { searchTherapistsAsync.run(); }}
                            />
                        </div>
                    </div>


                </div>

                {/* Results Table */}
                {searchData !== null && searchData !== undefined && searchData.length > 0 && (
                    <div className='results-table'>
                        <div className='overflow-hidden border-b border-gray-200 rounded-lg shadow'>
                            <DataTable
                                key={`datatable_${searchData.length}`}
                                columns={columns()}
                                data={searchData}
                                showPagination={false}
                                skipFilter={true}
                                customPageSize={pageSize}
                                getRowProps={(row: any) => ({
                                    onClick: () => history.push(`/admin/therapists/${row.values.userId}`),
                                })}
                            />
                        </div>
                    </div>
                )}

                {/* Pagination Row */}
                <div className='flex items-end justify-between p-1 mt-3 bg-white rounded-lg'>
                    {totalRecordCount > 0 &&
                        <Pagination
                            key={`page_${pageNumber}`} // This is critical to parent/child communication
                            totalRecordCount={totalRecordCount}
                            recordLimit={pageSize === -1 ? totalRecordCount : pageSize}
                            parentCurrentPage={pageNumber}
                            changeHandler={handlePageChange}
                        />
                    }
                </div>

                <div className='w-full mb-2 placeholder '>
                    <PageSizeSelector
                        currentPageSize={pageSize}
                        options={therapistPageSizeOptions}
                        changeHandler={handlePageSizeChange}
                        outerDivClasses='text-right'
                    />
                </div>

                {/* No therapists found message */}
                {searchData.length === 0 && (
                    <div>
                        <label>No therapists found.</label>
                        <div className='px-4 py-3 bg-gray-100 sm:px-6 sm:flex sm:flex-row-reverse'>

                        </div>
                    </div>
                )}
            </Fragment>
        </Layout>

    );
};

export default SearchTherapists;

export const PageSizeSelector = ({ options, outerDivClasses, changeHandler, currentPageSize }: PageSizeProps) => {
    return (
        <div className={outerDivClasses}>
            <p className='text-sm font-bold'>Records Per Page</p>
            <select className='w-40 mt-2' name='pageSizeSelector' id='pageSizeSelector'
                onChange={(e) => changeHandler(e.target.value)}
                value={currentPageSize}
            >
                {options.sort((a, b) => {
                    return a - b;
                }).map((item, idx) => <option key={`pageSize_${item}`}>{item}</option>)}
                {/*<option key={`pageSize-show-all`} value={`-1`}>Show All</option>*/}
            </select>
        </div >
    );
}